// @ts-check
/** @jsx jsx */
/** @jsxFrag React.Fragment */
import {
  Box,
  Button,
  Container,
  P,
  Text,
  Title,
} from '@bottlebooks/gatsby-theme-base';
import useLink from '@bottlebooks/gatsby-theme-event/src/useLink';
import { Trans } from '@lingui/macro';
import React from 'react';
import { jsx } from 'theme-ui';
import {
  ExploreAllProductsBanner,
  FavoriteExhibitor,
  FavoriteProduct,
  ListContainer,
  PersonalPageLayout,
  useEnrichedFavorites,
  useFavorites,
} from '..';
import useSiteConfig from '../../../../src/components/useSiteConfig';

export default function FavoritesPage({ locale }) {
  return (
    <PersonalPageLayout locale={locale}>
      <Content locale={locale} />
    </PersonalPageLayout>
  );
}

function Content({ locale }) {
  const { favorites, isLoading, error } = useFavorites();
  if (isLoading) return <Loading />;
  if (error) return <Error error={error} />;
  if (!favorites?.length) return <EmptyState />;
  return <Favorites locale={locale} />;
}

/**
 * @param {JSX.IntrinsicAttributes & { [x: string]: any; }} rest
 */
function Loading(rest) {
  return (
    <>
      <ListContainer {...rest}>
        <Text>
          <Trans>Loading…</Trans>
        </Text>
      </ListContainer>
      <ExploreAllProductsBanner />
    </>
  );
}

/** @param {{ error: Error; [x: string]: any }} props */
function Error({ error, ...rest }) {
  return (
    <>
      <ListContainer>
        <Text sx={{ color: 'brand.warningRed' }}>
          <Trans>An error has occurred.</Trans>
        </Text>
        <Text variant="small">{error.message}</Text>
      </ListContainer>
      <ExploreAllProductsBanner />
    </>
  );
}

/**
 * @param {JSX.IntrinsicAttributes} rest
 */
function EmptyState(rest) {
  const link = useLink();
  return (
    <ListContainer sx={{ textAlign: [null, 'center'] }} {...rest}>
      <Title sx={{ marginTop: 5, marginBottom: 4 }}>
        <Trans>No products saved.</Trans>
      </Title>
      <P variant="large" sx={{ margin: 'auto' }}>
        <Trans>
          You haven't saved any products to taste. Start your favorites list by
          saving some products.
        </Trans>
      </P>
      <Button variant="primary" to={link.products()} sx={{ marginTop: 4 }}>
        <Trans>Explore all products</Trans>
      </Button>
    </ListContainer>
  );
}

/**
 * @param {{ locale: string; }} props
 */
function Favorites({ locale }) {
  const link = useLink();
  const favorites = useEnrichedFavorites();
  const { listsEnabled } = useSiteConfig();
  if (!favorites?.length) return null;
  return (
    <Box sx={{ backgroundColor: 'light' }}>
      <Container sx={{ paddingTop: 5, paddingBottom: 2 }}>
        <Title variant="small">
          <Trans>My Favorites</Trans>
        </Title>
      </Container>
      <ListContainer>
        {favorites?.map((favorite) =>
          favorite.type === 'product' ? (
            <FavoriteProduct
              key={favorite.id}
              productId={favorite.productId}
              to={
                listsEnabled
                  ? `/${locale}/my/exhibitors/${favorite.exhibitorId}/${favorite.productId}`
                  : link.product({
                      productId: favorite.productId,
                      exhibitorId: favorite.exhibitorId,
                    })
              }
              sx={{
                boxShadow: 'borderSecondary',
                marginBottom: 'px', // This is a hack to get the box shadows aligned. Somewhere in the list item there's a negative margin.
              }}
            />
          ) : (
            favorite.type === 'registration' && (
              <Box key={favorite.id}>
                <FavoriteExhibitor
                  exhibitor={favorite}
                  to={
                    listsEnabled
                      ? `${locale}/my/exhibitors/${favorite.exhibitorId}`
                      : link.exhibitor({ exhibitorId: favorite.exhibitorId })
                  }
                  sx={{
                    boxShadow: 'borderSecondary',
                    marginBottom: 'px', // This is a hack to get the box shadows aligned. Somewhere in the list item there's a negative margin.
                  }}
                />
              </Box>
            )
          )
        )}
      </ListContainer>
      <ExploreAllProductsBanner />
    </Box>
  );
}
